import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { WEBSITE } from "../constants/regex";
import { filterBySearchValue, filterBookmarked, getDataIntercom, enableScroll } from "./index";
import { QUESTION_TYPES } from "../constants/question";
import { actions as listingActions } from "../actions/listing";
import { actions as paymentActions } from "../actions/payment";
import { actions as teamsActions } from "../actions/teams";
import { indexEdit } from "../constants/listings";
import { findMemberAccess, getProfileDisplayName, resolveTeamPermissions } from "./teamPermissions";

/**
 * Debounced callback helper for autosave flows.
 *
 * Returns a stable `debounced` function that defers the latest callback by `delay`ms,
 * a `flush` function that cancels any pending timer and runs immediately (use on blur /
 * mouse-leave / unmount-critical paths), and a `cancel` function. The callback is kept in
 * a ref so the debounced/flush identities stay stable across renders and always call the
 * most recent closure (avoids stale Formik values).
 */
export const useDebouncedCallback = (callback, delay = 500) => {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef(null);

  useEffect(() => {
    callbackRef.current = callback;
  });

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const debounced = useCallback(
    (...args) => {
      cancel();
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        callbackRef.current(...args);
      }, delay);
    },
    [cancel, delay]
  );

  const flush = useCallback(
    (...args) => {
      cancel();
      return callbackRef.current(...args);
    },
    [cancel]
  );

  // Clear any pending timer on unmount to avoid setting state on an unmounted component.
  useEffect(() => cancel, [cancel]);

  return { debounced, flush, cancel };
};

/**
 * Persist pending editor state when the document is leaving or the component unmounts.
 * `beforeunload` / `pagehide` cover tab close and refresh; cleanup covers in-app navigation
 * that never fires those events (SEC-011).
 */
export const useUnloadSafePersist = persist => {
  const persistRef = useRef(persist);
  persistRef.current = persist;

  useEffect(() => {
    const flush = () => persistRef.current?.();

    window.addEventListener("beforeunload", flush);
    window.addEventListener("pagehide", flush);

    return () => {
      window.removeEventListener("beforeunload", flush);
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, []);
};

export const useDocTitle = (title, keepOnUnmount = false) => {
  const defaultTitle = useRef(document.title);
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t(title);
  }, [t, title]);

  useEffect(() => {
    return () => {
      if (!keepOnUnmount) {
        document.title = defaultTitle.current;
      }
    };
  }, [keepOnUnmount]);
};

export const useNotification = (error, success) => {
  const [notes, setNotes] = useState({ success: null, error: null });

  useEffect(() => {
    if (Object.values(success).some(value => !!value)) {
      setNotes({ success: true });
      setTimeout(() => setNotes({ success: null }), 7000);
    }

    if (error) {
      setNotes({ error });
      setTimeout(() => setNotes({ error: null }), 7000);
    }
  }, [success, error]);

  return notes;
};

// question-answer page
export const useFiltrationQAndAPage = (activeFilters, searchValue, bookmarked, setQuestionsToRender) => {
  const { qa, answers, allQuestionAndAnswers } = useSelector(state => state.questions);

  useEffect(() => {
    if (!activeFilters.isFirstRender) {
      const { practices, categories, professions } = activeFilters;

      const filterByWebsiteUrl = item => categories.includes(item.website_url.replace(WEBSITE, "").split("/")[0]);
      const filterByPractices = ({ practice1, practice2, practice3 }) => {
        const toArrayPractices = [practice1, practice2, practice3];
        return practices.some(item => toArrayPractices.indexOf(item) >= 0);
      };
      const filterByProfessions = ({ profession_id: professionId }) => professions.includes(professionId);
      const filterByAllQuestions = item => filterByProfessions(item) && filterByWebsiteUrl(item);
      const filterByAllAnswers = item => filterByPractices(item) && filterByWebsiteUrl(item);

      let allAnswerAndQuestionsList = [];
      let questionsList = [];
      let answersList = [];

      if (practices.length && categories.length && professions.length) {
        answersList = filterBySearchValue(answers, searchValue).filter(filterByAllAnswers);
        questionsList = filterBySearchValue(qa, searchValue).filter(filterByAllQuestions);
        allAnswerAndQuestionsList = filterBySearchValue(allQuestionAndAnswers, searchValue).filter(filterByAllQuestions);
      } else if (practices.length && categories.length) {
        answersList = filterBySearchValue(answers, searchValue).filter(filterByAllAnswers);
        questionsList = filterBySearchValue(qa, searchValue);
        allAnswerAndQuestionsList = filterBySearchValue(allQuestionAndAnswers, searchValue);
      } else if (professions.length && categories.length) {
        questionsList = filterBySearchValue(qa, searchValue).filter(filterByAllQuestions);
        answersList = filterBySearchValue(answers, searchValue);
        allAnswerAndQuestionsList = filterBySearchValue(allQuestionAndAnswers, searchValue);
      } else if (professions.length && practices.length) {
        questionsList = filterBySearchValue(qa, searchValue).filter(filterByProfessions);
        answersList = filterBySearchValue(answers, searchValue).filter(filterByPractices);
        allAnswerAndQuestionsList = filterBySearchValue(allQuestionAndAnswers, searchValue);
      } else if (professions.length) {
        questionsList = filterBySearchValue(qa, searchValue).filter(filterByProfessions);
        answersList = filterBySearchValue(answers, searchValue);
        allAnswerAndQuestionsList = filterBySearchValue(allQuestionAndAnswers, searchValue);
      } else if (practices.length) {
        answersList = filterBySearchValue(answers, searchValue).filter(filterByPractices);
        questionsList = filterBySearchValue(qa, searchValue);
        allAnswerAndQuestionsList = filterBySearchValue(allQuestionAndAnswers, searchValue);
      } else if (categories.length) {
        questionsList = filterBySearchValue(qa, searchValue).filter(filterByWebsiteUrl);
        answersList = filterBySearchValue(answers, searchValue).filter(filterByWebsiteUrl);
        allAnswerAndQuestionsList = filterBySearchValue(allQuestionAndAnswers, searchValue);
      } else {
        questionsList = filterBySearchValue(qa, searchValue);
        answersList = filterBySearchValue(answers, searchValue);
        allAnswerAndQuestionsList = filterBySearchValue(allQuestionAndAnswers, searchValue);
      }

      setQuestionsToRender({
        qa: filterBookmarked(bookmarked, questionsList),
        answers: filterBookmarked(bookmarked, answersList),
        all: filterBookmarked(bookmarked, allAnswerAndQuestionsList)
      });
    }
    // eslint-disable-next-line
  }, [activeFilters.practices, activeFilters.categories, activeFilters.professions, searchValue]);
};

// question-answer detail page
export const useUpdateQuestionsIds = (id, tab) => {
  const [prevQuestionId, setPrevQuestionId] = useState(null);
  const [nextQuestionId, setNextQuestionId] = useState(null);
  const { qa, answers } = useSelector(state => state?.questions);
  const answeredQuestions = qa.filter(item => Object.keys(answers).includes(item.question_id));
  const currentQuestionIdx = useMemo(() => qa.findIndex(item => String(item.question_id) === String(id)), [qa, qa.length, id]);

  const getPrevNextQuestionsId = useCallback(
    questionsArr => {
      setPrevQuestionId(questionsArr?.[currentQuestionIdx - 1]?.question_id);
      setNextQuestionId(questionsArr?.[currentQuestionIdx + 1]?.question_id);
    },
    [currentQuestionIdx]
  );

  useEffect(() => {
    if (!tab || tab === QUESTION_TYPES.QUESTIONS) {
      getPrevNextQuestionsId(qa);
    }
    if (tab === QUESTION_TYPES.MY_ANSWER) {
      getPrevNextQuestionsId(answeredQuestions);
    }
  }, [qa, tab, id]);

  return { prevQuestionId, nextQuestionId };
};

export const useIntercom = ({ isLoggedIn, listings, profile }) => {
  useEffect(() => {
    if (window.Intercom) {
      if (profile?.user_id && profile.name_f) {
        window.Intercom("boot", {
          api_base: "https://api-iam.intercom.io",
          app_id: "lm1evs0k",
          user_id: profile.user_id,
          user_hash: profile.intercom_hash,
          name: `${profile.name_f} ${profile.name_l}`,
          email: profile.email,
          created_at: profile.added
        });
      }

      if (isLoggedIn === true) {
        getDataIntercom(listings);
      }

      window.Intercom("update");
    }
  }, [isLoggedIn, profile?.user_id]); // eslint-disable-line
};

export const useKeyDownEscape = callBack => {
  const handleKeyDown = useCallback(
    event => {
      if (event.key === "Escape") {
        callBack();
      }
    },
    [] // eslint-disable-line
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, false);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, false);
    };
  }, [handleKeyDown]);
};

export const useOnclickOutside = (ref, handler, reset) => {
  useEffect(() => {
    const listener = event => {
      event.stopPropagation();
      if (ref.current && !ref.current.contains(event.target)) {
        handler();
      }
    };

    document.addEventListener("mousedown", listener);

    return () => {
      if (!reset) handler();
      document.removeEventListener("mousedown", listener);
    };
  }, [ref, handler]);
};

export const useKeyDownEnter = callBack => {
  const handleKeyDown = useCallback(
    event => {
      if (event.key === "Enter") {
        callBack(event);
      }
    },
    [callBack] // eslint-disable-line
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, false);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, false);
    };
  }, [handleKeyDown]);
};

export const useChangePage = callBack => {
  const currentPage = useRef("");
  const location = useLocation();

  useEffect(() => {
    if (Boolean(currentPage.current) && currentPage.current !== location.pathname) {
      callBack();
    }

    if (currentPage.current !== location.pathname) {
      currentPage.current = location.pathname;
    }
  }, [location.pathname, currentPage]); // eslint-disable-line
};

export const useDebounce = (callBack, isSendHide, delay = 1500) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isHide, setIsHide] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      callBack(searchTerm);
      if (isSendHide) {
        if (searchTerm && isHide) {
          setIsHide(false);
        }

        if (!isHide && !searchTerm) {
          setIsHide(true);
        }
      }
    }, delay);

    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [searchTerm]); // eslint-disable-line

  return { searchTerm, setSearchTerm, isHide };
};

export const useValidationError = (errors, errorName) => {
  const dispatch = useDispatch();
  const { validationErrors } = useSelector(state => state.listing);
  const setValidationError = useCallback(error => dispatch(listingActions.setValidationError(error)), [dispatch]);

  useEffect(() => {
    if (errors !== "isRemove" && Object.values(errors).length && errors[errorName] !== validationErrors[errorName]) {
      setValidationError(errors);
    }

    if (errors === "isRemove" && validationErrors[errorName]) {
      const { [errorName]: value, ...rest } = validationErrors;

      setValidationError(rest);
    }
  }, [errors]); // eslint-disable-line
};

const KEEP_SECTION_VALIDATION_ERROR = {};

/**
 * Clears a repeatable section's `listing.validationErrors` entry once the section has no rows left.
 * Each row reports the section's error through useValidationError, so after the last row is removed
 * no row is mounted to clear it and Publish stays blocked until a reload (GROWTH-6982).
 *
 * @param {number} rowCount Rows the section currently renders.
 * @param {string} errorName The section's key in `listing.validationErrors`.
 */
export const useClearValidationErrorWithoutRows = (rowCount, errorName) =>
  useValidationError(rowCount ? KEEP_SECTION_VALIDATION_ERROR : "isRemove", errorName);

export const useKeycloakAuth = ({ keycloakInstance, setLoggedIn }) => {
  const initialized = useRef(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [initializedKeycloak, setInitializedKeycloak] = useState(false);

  useEffect(() => {
    if (initialized.current) return;

    const initKeycloak = async () => {
      try {
        const isAuthenticated = await keycloakInstance.init({
          checkLoginIframe: false,
          onLoad: "check-sso",
          pkceMethod: "S256"
        });

        setInitializedKeycloak(true);

        setAuthenticated(isAuthenticated);
        if (isAuthenticated) {
          setLoggedIn(true);
        }
      } catch (error) {
        console.error("Failed to initialize Keycloak:", error);
      }
    };

    initKeycloak();
    initialized.current = true;
  }, [keycloakInstance]);

  return { keycloakInstance, authenticated, initializedKeycloak };
};

export const useMembership = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const { modals } = useSelector(state => state.listing);
  const deletePrimaryImage = useCallback(listingId => dispatch(listingActions.deletePrimaryImage(listingId, pathname)), [
    dispatch,
    pathname
  ]);
  const getMemberDataNew = useCallback(
    (listingId, options = {}) => dispatch(listingActions.getMemberDataNew(listingId, pathname, navigate, false, options)),
    [dispatch, pathname, navigate]
  );
  const deleteAdditionalImages = useCallback(
    (listingId, imageId) => dispatch(listingActions.deleteAdditionalImages(listingId, imageId, pathname)),
    [dispatch, pathname]
  );
  const deleteCoverImage = useCallback((listingId, imageId) => dispatch(listingActions.deleteCoverImage(listingId, imageId, pathname)), [
    dispatch,
    pathname
  ]);

  const getValidations = useCallback(
    (listingId, options) => dispatch(listingActions.getValidations(listingId, pathname, navigate, options)),
    [dispatch, navigate, pathname]
  );
  const getNotificationTypes = useCallback(
    (listingId, listingKind) => dispatch(listingActions.getNotificationTypes(listingId, listingKind)),
    [dispatch]
  );
  const getDirectMessages = useCallback(listingId => dispatch(listingActions.getDirectMessages(listingId)), [dispatch]);
  const getDonationsSettings = useCallback(listingId => dispatch(listingActions.getDonationsSettings(listingId)), [dispatch]);
  const requestDonations = useCallback(listingId => dispatch(listingActions.requestDonations(listingId)), [dispatch]);
  const connectDonationsStripe = useCallback(listingId => dispatch(listingActions.connectDonationsStripe(listingId)), [dispatch]);
  const disconnectDonationsStripe = useCallback(listingId => dispatch(listingActions.disconnectDonationsStripe(listingId)), [dispatch]);
  const getListingSectionHide = useCallback(listingId => dispatch(listingActions.getListingSectionHide(listingId)), [dispatch]);
  const updateListingSectionHide = useCallback(
    (listingId, listingSectionId, hidden) => dispatch(listingActions.updateListingSectionHide(listingId, listingSectionId, hidden)),
    [dispatch]
  );
  const setMembershipClear = useCallback(() => dispatch(listingActions.setMembershipClear()), [dispatch]);

  const submitChanges = useCallback(
    (memberData, section, isCloseModals, isReload) =>
      dispatch(listingActions.submitChanges(memberData, section, pathname, isCloseModals, isReload, navigate)),
    [dispatch, navigate, pathname]
  );

  const autoSaveListing = useCallback(
    (memberData, section, command, abortController) =>
      dispatch(listingActions.autoSaveListing(memberData, section, pathname, command, abortController, navigate)),
    [dispatch, navigate, pathname]
  );

  const submitImages = useCallback(
    (memberData, section) => dispatch(listingActions.submitImages(memberData, section, pathname, navigate)),
    [dispatch, navigate, pathname]
  );
  const saveDirectMessages = useCallback(data => dispatch(listingActions.saveDirectMessages(data)), [dispatch]);

  const addCard = useCallback((cardData, isFirst) => dispatch(paymentActions.addCard(cardData, isFirst)), [dispatch]);
  const cleanUpgradeOptions = useCallback(() => dispatch(listingActions.cleanUpgradeOptions()), [dispatch]);

  const getDirectoriesProfessions = useCallback(() => dispatch(listingActions.getDirectoriesProfessions()), [dispatch]);
  const pauseMembership = useCallback(id => dispatch(listingActions.pauseListing(id, false, pathname, true, false, navigate)), [
    dispatch,
    navigate,
    pathname
  ]);
  const unpauseMembership = useCallback(id => dispatch(listingActions.unPauseListing(id, true, false, pathname, false, navigate)), [
    dispatch,
    navigate,
    pathname
  ]);
  const getUpgradeOptions = useCallback((id, coupon) => dispatch(listingActions.getUpgradeOptions(id, coupon)), [dispatch]);

  const getPendingPayment = useCallback(id => dispatch(listingActions.getPendingPayment(id)), [dispatch]);
  const getRenewOptions = useCallback((id, coupon) => dispatch(listingActions.getRenewOptions(id, coupon)), [dispatch]);

  const updateCard = useCallback(card => dispatch(paymentActions.updateCard(card)), [dispatch]);
  const publishListing = useCallback(id => dispatch(listingActions.publishListing(id, false, pathname, navigate)), [
    dispatch,
    navigate,
    pathname
  ]);
  const clearSuccess = useCallback(() => dispatch(listingActions.clearSuccess()), [dispatch]);
  const clearErrorState = useCallback(() => dispatch(listingActions.clearErrorState()), [dispatch]);
  const clearAllModals = useCallback(() => dispatch(listingActions.clearAllModals()), [dispatch]);
  const resetDraft = useCallback(listingId => dispatch(listingActions.resetDraft(listingId, pathname)), [dispatch, pathname]);
  const setCurrentTeam = useCallback(team => dispatch(teamsActions.setCurrentTeam(team)), [dispatch]);

  const getOptions = useCallback(() => {
    if (modals.payPending) {
      return getPendingPayment;
    }
    if (modals.upgrade) {
      return getUpgradeOptions;
    }
    return getRenewOptions;
  }, [getPendingPayment, getRenewOptions, getUpgradeOptions, modals.payPending, modals.upgrade]);

  return {
    deletePrimaryImage,
    getMemberDataNew,
    deleteAdditionalImages,
    deleteCoverImage,
    getValidations,
    getNotificationTypes,
    getDirectMessages,
    getDonationsSettings,
    requestDonations,
    connectDonationsStripe,
    disconnectDonationsStripe,
    getListingSectionHide,
    updateListingSectionHide,
    setMembershipClear,
    cleanUpgradeOptions,
    publishListing,
    resetDraft,
    getDirectoriesProfessions,
    pauseMembership,
    unpauseMembership,
    addCard,
    updateCard,
    submitImages,
    saveDirectMessages,
    submitChanges,
    autoSaveListing,
    clearSuccess,
    clearErrorState,
    clearAllModals,
    setCurrentTeam,
    getOptions
  };
};

const LISTING_EDIT_SECTION_TITLE_KEYS = {
  details: "dashboard_listing_edit_tab_details",
  licenses: "dashboard_listing_edit_tab_licenses",
  bio: "dashboard_listing_edit_tab_bio",
  donations: "dashboard_listing_edit_tab_donations",
  location: "dashboard_listing_edit_tab_location",
  publications: "dashboard_listing_edit_tab_publications",
  messages: "dashboard_listing_edit_tab_messages",
  notifications: "dashboard_listing_edit_tab_notifications"
};

const LISTING_EDIT_TYPE_TITLE_KEYS = {
  Profiles: "dashboard_profile_edit_section_metatitle",
  People: "dashboard_profile_edit_section_metatitle",
  Organizations: "dashboard_organization_edit_section_metatitle",
  Events: "dashboard_event_edit_section_metatitle",
  Jobs: "dashboard_job_edit_section_metatitle"
};

/**
 * Sets the listing editor document.title from listing type and the active tab.
 * All user-facing strings resolve through dashboard_* i18n keys.
 *
 * @param {Object} [params]
 * @param {string} [params.listingType]
 * @param {string} [params.currentTabPath] Active editor tab (details, bio, licenses, …)
 */
export const useMembershipDocumentTitle = ({ listingType, currentTabPath } = {}) => {
  const { t } = useTranslation();

  useEffect(() => {
    const isJobListing = listingType === "Jobs";
    const isEvents = listingType === "Events";
    const isProfiles = listingType === "Profiles" || listingType === "People";
    const isOrganizationsPage = listingType === "Organizations";

    const typeKey = LISTING_EDIT_TYPE_TITLE_KEYS[listingType];
    const isBioDescription = currentTabPath === "bio" && (isEvents || isJobListing);
    const sectionKey = isBioDescription ? "dashboard_listing_edit_tab_description" : LISTING_EDIT_SECTION_TITLE_KEYS[currentTabPath];

    if (typeKey && sectionKey) {
      document.title = t(typeKey, { section: t(sectionKey) });
      return;
    }

    if (isEvents) {
      document.title = t("dashboard_events_edit_metatitle");
      return;
    }

    if (isJobListing) {
      document.title = t("dashboard_jobs_edit_metatitle");
      return;
    }

    if (isOrganizationsPage) {
      document.title = t("dashboard_organizations_edit_metatitle");
      return;
    }

    document.title = isProfiles ? t("dashboard_profile_edit_metatitle") : t("dashboard_listings_metatitle");
  }, [listingType, currentTabPath, t]);
};

export const useMembershipImageUpload = () => {
  const location = useLocation();
  const { pathname } = location;
  const dispatch = useDispatch();

  const deleteCoverImage = useCallback(
    (listingId, imageId, section) => dispatch(listingActions.deleteCoverImage(listingId, imageId, pathname, section)),
    [dispatch, pathname]
  );

  const [showUploadImageNotificationError, setShowUploadImageNotificationError] = useState(false);
  const [uploadImageNotificationErrorText, setUploadImageNotificationErrorText] = useState("");

  const uploadImagesError = (isShow, isSizeError, isWidthTooSmall, isLogoTooSmall) => {
    setShowUploadImageNotificationError(isShow);

    if (isSizeError) {
      setUploadImageNotificationErrorText("It seems the file is too large. Please upload an image under 5 MB");
    } else if (isWidthTooSmall) {
      setUploadImageNotificationErrorText("The image is too small. Minimum width: 1200px");
    } else if (isLogoTooSmall) {
      setUploadImageNotificationErrorText("The image is too small. Minimum size: 200 x 200px");
    } else {
      setUploadImageNotificationErrorText("");
    }
  };

  const handleDeleteCoverImage = (listingId, imageId, section) => {
    return deleteCoverImage(listingId, imageId, section);
  };

  return {
    uploadImagesError,
    showUploadImageNotificationError,
    uploadImageNotificationErrorText,
    handleDeleteCoverImage
  };
};

export const useMembershipModal = ({ setMemberData, memberData }) => {
  const dispatch = useDispatch();

  const [indexToEdit, setIndexToEdit] = useState(indexEdit);
  const toggleActivityOfModal = (modal, isActive) => dispatch(listingActions.toggleActivityOfModal(modal, isActive));
  const activeNotificationModal = (modal, data) => dispatch(listingActions.activeNotificationModal(modal, data));

  const openModal = (modalName, editTarget) => {
    if (editTarget) {
      setIndexToEdit({ ...indexToEdit, [modalName]: typeof editTarget !== "string" ? editTarget : editTarget.toString() });
    } else {
      setIndexToEdit(indexEdit);
    }
    toggleActivityOfModal(modalName, true);
  };

  const closeModal = modalName => {
    setIndexToEdit({ ...indexToEdit, [modalName]: null });
    setMemberData(memberData);
    toggleActivityOfModal(modalName, false);
  };

  const openAddNotificationsModal = modalName => {
    toggleActivityOfModal(modalName, true);
  };

  const handleActiveNotificationModal = (modalName, data) => {
    activeNotificationModal(modalName, data);
  };

  return { openModal, closeModal, openAddNotificationsModal, handleActiveNotificationModal };
};

export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 767);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");

    const handleChange = () => {
      setIsMobile(mediaQuery.matches);
    };

    handleChange();

    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  return isMobile;
};

/**
 * True while the viewport is at least `minWidth` px wide: the same test as a CSS `@media (min-width: …)` rule, for markup
 * that has to follow a breakpoint (the filter panels that are collapsed below it and always shown from it).
 * Without matchMedia (jsdom) it falls back to window.innerWidth.
 */
export const useMinWidth = minWidth => {
  const [matches, setMatches] = useState(() => window.innerWidth >= minWidth);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mediaQuery = window.matchMedia(`(min-width: ${minWidth}px)`);
    const handleChange = () => setMatches(mediaQuery.matches);
    handleChange();
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [minWidth]);

  return matches;
};

/**
 * Hook to handle file input with scroll management
 * Detects when file dialog is canceled and re-enables scroll
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoOpen - Whether to automatically open file dialog on mount
 * @param {number} options.autoOpenDelay - Delay in ms before auto-opening (default: 100)
 * @returns {Object} - { inputRef, openFileDialog, handleFileChange }
 */
export const useFileInputWithScroll = ({ autoOpen = false, autoOpenDelay = 100 } = {}) => {
  const inputRef = useRef(null);
  const isDialogOpenRef = useRef(false);
  const timeoutRef = useRef(null);
  const fileCountRef = useRef(0);

  const openFileDialog = useCallback(() => {
    if (inputRef.current && !isDialogOpenRef.current) {
      // Store current file count before opening dialog
      fileCountRef.current = inputRef.current.files?.length || 0;
      isDialogOpenRef.current = true;
      inputRef.current.click();
    }
  }, []);

  const handleFileChange = useCallback(event => {
    // Reset dialog state when file is selected
    if (event.target.files && event.target.files.length > 0) {
      isDialogOpenRef.current = false;
    }
  }, []);

  // Handle window focus and visibility change to detect when file dialog closes (canceled)
  useEffect(() => {
    const checkAndEnableScroll = () => {
      if (isDialogOpenRef.current && inputRef.current) {
        // Use a small delay to check if files were actually selected
        timeoutRef.current = setTimeout(() => {
          const currentFileCount = inputRef.current?.files?.length || 0;
          // If dialog was open and no new files were selected, it was canceled
          if (isDialogOpenRef.current && currentFileCount === fileCountRef.current) {
            isDialogOpenRef.current = false;
            enableScroll();
          }
        }, 150);
      }
    };

    const handleWindowFocus = () => {
      checkAndEnableScroll();
    };

    // Also handle visibility change as a fallback
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkAndEnableScroll();
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (autoOpen && inputRef.current) {
      const timeout = setTimeout(() => {
        openFileDialog();
      }, autoOpenDelay);

      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [autoOpen, autoOpenDelay, openFileDialog]);

  return {
    inputRef,
    openFileDialog,
    handleFileChange
  };
};

export const useTeamContextKey = () => {
  const location = useLocation();
  const currentTeam = useSelector(state => state.teams.currentTeam);
  const member = useSelector(state => state.teams.member);
  const memberLoaded = useSelector(state => state.teams.memberLoaded);
  const urlOwnerId = useMemo(() => {
    const userId = new URLSearchParams(location.search).get("user_id");
    return userId ? String(userId) : "";
  }, [location.search]);
  const currentOwnerId = currentTeam?.value ? String(currentTeam.value) : "";

  return useMemo(() => {
    const requestedOwnerId = urlOwnerId || currentOwnerId;

    if (!requestedOwnerId) {
      return { teamContextKey: "", isTeamContextReady: true };
    }

    // Team chosen via dropdown (Redux only) — do not wait for member list hydration
    if (!urlOwnerId && currentOwnerId) {
      return { teamContextKey: currentOwnerId, isTeamContextReady: true };
    }

    if (urlOwnerId && !memberLoaded) {
      return { teamContextKey: requestedOwnerId, isTeamContextReady: false };
    }

    // If the member list failed to hydrate, keep using the URL owner id so
    // team-scoped API calls still request the correct account.
    if (!member?.length) {
      return { teamContextKey: requestedOwnerId, isTeamContextReady: true };
    }

    const access = findMemberAccess(member, requestedOwnerId, { activeOnly: true });
    if (!access) {
      return { teamContextKey: requestedOwnerId, isTeamContextReady: true };
    }

    if (urlOwnerId && currentOwnerId !== requestedOwnerId) {
      return { teamContextKey: requestedOwnerId, isTeamContextReady: false };
    }

    return { teamContextKey: requestedOwnerId, isTeamContextReady: true };
  }, [urlOwnerId, currentOwnerId, member, memberLoaded, currentTeam?.role]);
};

export const useTeamPermissions = () => {
  const currentTeam = useSelector(state => state.teams.currentTeam);
  const member = useSelector(state => state.teams.member);
  const { user_id: profileUserId } = useSelector(state => state.account.profile);

  return useMemo(() => {
    const ownerId = currentTeam?.value || null;

    if (!ownerId) {
      return resolveTeamPermissions({
        profileUserId,
        ownerId: null,
        role: null
      });
    }

    const access = findMemberAccess(member, ownerId, { activeOnly: true });
    const role = access?.role != null ? Number(access.role) : null;

    return resolveTeamPermissions({
      profileUserId,
      ownerId,
      role
    });
  }, [currentTeam?.value, currentTeam?.role, member, profileUserId]);
};

/**
 * Returns a callback that leaves a team context: selects the member's own account and drops `user_id` from the URL,
 * keeping the rest of the query string and the hash. The team change resets the payment slice (reducers/payment.js).
 *
 * @param {Object} profile The member's own profile; with no name and no email the account label is the bundle's
 * `dashboard_common_selectteam_myaccount` ("My Account").
 */
export const useSwitchToOwnAccount = profile => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const ownAccountLabel = getProfileDisplayName(profile, t("dashboard_common_selectteam_myaccount"));

  return useCallback(() => {
    dispatch(teamsActions.setCurrentTeam({ value: "", label: ownAccountLabel, role: null }));
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("user_id");
    navigate({
      pathname: location.pathname,
      search: searchParams.toString(),
      hash: location.hash
    });
  }, [dispatch, ownAccountLabel, location.pathname, location.search, location.hash, navigate]);
};

export { useFocusTrap } from "./focusTrap";
export { useRowFocusLeave } from "./rowFocusLeave";
